import { useQuery } from '@zg-rentals/react-query5';

import type { UseQueryOptions } from '@zg-rentals/react-query5';

import { logError } from '@zg-rentals/log-error';

import { RentalManagerAccountViewZrmV2WebApiHandler_leases } from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { AccountViewTableCellDto, ListingAlias } from '@zg-rentals/ts-rental-manager-api-beta-client';

// LeasesParams is not exported from the API client, so it's copied here.
type LeasesParams = {
  groupId: string;
  includeActive?: boolean;
  includeArchived?: boolean;
  includeInactive?: boolean;
  includeUnarchived?: boolean;
  listingAlias?: ListingAlias;
  searchText?: string;
};

function getLeasesApiParams(params: LeasesParams): LeasesParams {
  const apiParams = { ...params };

  // construct the api params object
  if (!apiParams.listingAlias) delete apiParams.listingAlias;
  if (!apiParams.searchText) delete apiParams.searchText;
  if (apiParams.includeActive === undefined) delete apiParams.includeActive;
  if (apiParams.includeInactive === undefined) delete apiParams.includeInactive;
  if (apiParams.includeArchived === undefined) delete apiParams.includeArchived;
  if (apiParams.includeUnarchived === undefined) delete apiParams.includeUnarchived;

  return apiParams;
}

type LeasesQueryOptions = Omit<UseQueryOptions<AccountViewTableCellDto, Error>, 'queryKey' | 'queryFn'>;

export const useGetPropertyLeases = (params: LeasesParams, options?: LeasesQueryOptions) => {
  const {
    groupId,
    listingAlias,
    includeActive,
    includeInactive,
    includeArchived,
    includeUnarchived,
    searchText = '',
  } = params;

  const { data, isPending, isError, isFetching, refetch } = useQuery<AccountViewTableCellDto, Error>({
    queryKey: [
      'getLeases',
      groupId,
      listingAlias,
      searchText,
      includeActive,
      includeInactive,
      includeArchived,
      includeUnarchived,
    ],
    queryFn: async () => {
      if (!params.groupId?.trim()) {
        logError({
          error: new Error('useGetPropertyLeases: groupId is required'),
          context: 'useGetPropertyLeases',
        });

        throw new Error('groupId is required');
      }

      const apiParams = getLeasesApiParams(params);

      return RentalManagerAccountViewZrmV2WebApiHandler_leases(apiParams);
    },
    // Keep previous data during refetch to prevent UI flicker
    placeholderData: (previousData) => previousData,
    ...options,
  });

  return {
    data,
    isPending,
    isFetching,
    isError,
    refetch,
  };
};
